<template>
  <div class="thought-comments" role="list" :aria-label="domainStore.translate('discuss.comment.title')">
    <template v-if="showCommentForm && isInitialized">
      <comment-form
        :thoughtid="thought.id"
        :topicid="topic.id"
        :infotext="newCommentText"
        :withvote="newCommentVote"
        :comment="commentContent"
        @comment-form-close="onCommentFormClose()"
        @comment-updated="updatedMyComment"
        @comment-vote-sent="onVoteSentWithComment"
      ></comment-form>
    </template>
    <template v-else-if="myComment.exists">
      <comment
        :thoughtid="thought.id"
        :topicid="topic.id"
        :comment="myComment.comment"
        @comment-form-close="onCommentFormClose()"
        @comment-updated="updatedMyComment"
      />
    </template>

    <template v-if="isInitialized">
      <div
        v-for="comment in comments.highlighted"
        :id="commentIdTag(comment.commentid)"
        :key="comment.commentid"
        :class="{ highlighted: comment.highlight === 1 }"
        class="wdw-comment-item"
        role="listitem"
      >
        <comment
          :thoughtid="thought.id"
          :comment="comment"
          :topicid="topic.id"
          @comment-form-close="onCommentFormClose()"
          @comment-updated="updatedMyComment"
        />
      </div>
    </template>
    <template v-if="isInitialized && showNormalComments">
      <div
        v-for="comment in comments.normal"
        v-bind:key="comment.commentid"
        :id="commentIdTag(comment.commentid)"
        class="wdw-comment-item wdw-comment"
        role="listitem"
      >
        <comment
          :thoughtid="thought.id"
          :comment="comment"
          :topicid="topic.id"
          @comment-form-close="onCommentFormClose()"
          @comment-updated="updatedMyComment"
        />
      </div>
    </template>

    <div v-if="moreCommentsToLoad" class="wdw-comment load-more wdw-simple-box" role="listitem">
      <a
        @click="loadAllComments"
        @keyup.enter="loadAllComments"
        role="link"
        class="wdw-comment-text"
        :aria-label="domainStore.translate('discuss.comment.list.load-all-comments')"
        tabindex="0"
      >
        <font-awesome-icon icon="comments" size="lg" aria-hidden="true" />
        {{ domainStore.translate('discuss.comment.list.load-all-comments') }}
      </a>
    </div>
    <div v-if="loadingAllComments" class="comments-request-loading widget-loading" role="listitem">
      <vue-loading :loadingMessage="domainStore.translate('discuss.comment.message.loading')" />
    </div>
  </div>
</template>

<script>
import VueLoading from '@/components/general/Loading';
import Comment from './Comment.vue';
import CommentForm from '@/components/discuss/comment/CommentForm.vue';
// import CommentSupport from "./CommentSupport.vue"
import useComments from '@/composables/comment/useComments';
import useCommentEvents from '@/composables/comment/useCommentEvents';
import useToggler from '@/composables/general/useToggler';
import { toRefs, watch, computed, onMounted } from '@vue/runtime-core';
import { userStore } from '@/store/user-store';
import { thoughtStore } from '@/store/thought-store';
import { domainStore } from '@/store/domain-store';

export default {
  name: 'comment-list',

  props: {
    thought: {
      type: Object,
      required: true,
    },
    topic: {
      required: true,
      type: Object,
    },
    initCommentsFromThought: {
      type: Boolean,
      required: false,
      default: false,
    },
    loadAll: {
      type: Boolean,
      default: false,
    },
    showNewComment: {
      type: Boolean,
      default: false,
    },
    newCommentText: {
      required: false,
      type: String,
      default: '',
    },
    newCommentVote: {
      required: false,
      type: Number,
      default: 0,
    },
    // Used to signal this component that it has to refresh its content
    signalRefresh: {
      type: Number,
      required: false,
      default: 0,
    },
    onNewCommentFromList: {
      required: false,
      type: Boolean,
      default: false,
    },
    // surl: {
    //   required: true,
    //   type: String
    // }
  },

  emits: ['comment-form-close', 'comment-user-has-own', 'comment-updated', 'comment-vote-sent'],

  setup(props, context) {
    // -------------------CONSTANT---------------------
    const { signalRefresh, showNewComment, thought } = toRefs(props);
    const { emitCommentFormClose, emitCommentUpdated, emitCommentVoteSent } = useCommentEvents(context);
    const {
      isInitialized,
      myComment,
      comments,
      loadingAllComments,
      moreCommentsToLoad,
      showNormalComments,
      loadAllComments,
      refreshComments,
      isRefreshing,
    } = useComments(
      props.topic.id,
      props.thought,
      props.loadAll,
      props.initCommentsFromThought,
      props.topic.multiple_comments
    );
    const { toggleValue: showCommentForm, set: setshowCommentForm } = useToggler(false);
    // ------------------COMPUTED-----------------------
    const isLoggedIn = computed(() => {
      return userStore.isLoggedIn();
    });

    const commentContent = computed(() => {
      if (props.topic.multiple_comments) {
        return null;
      }
      return myComment && myComment.comment ? myComment.comment : null;
    });

    // ------------------FUNCTION-----------------------
    function commentIdTag(commentId) {
      return `comment-${commentId}`;
    }

    function onCommentFormClose() {
      setshowCommentForm(false);
      emitCommentFormClose();
    }
    function onCommentFormOpen(comment) {
      setshowCommentForm(true);
    }

    function onVoteSentWithComment(vote) {
      emitCommentVoteSent(vote);
    }
    function updatedMyComment(comment) {
      onCommentFormClose();
      if (props.topic.multiple_comments) {
        if (comments.normal.find(ele => ele.commentid == comment.commentid) == undefined) {
          comments.normal.push(comment);
        }
      } else if (comment !== null) {
        myComment.exists = true;
        myComment.comment = comment;
      } else {
        myComment.exists = false;
        myComment.comment = null;
      }
      emitCommentUpdated(comment);
      thoughtStore.fetchThought(thought.value.id, true);
    }

    // -------------------WATCHERS--------------------
    watch(showNewComment, (show, oldShow) => {
      if (show) {
        setshowCommentForm(true);
      }
    });
    watch(signalRefresh, (newVal, oldVal) => {
      if (!isRefreshing) {
        refreshComments(props.loadAll);
      }
    });

    // this kinda watcher is already implemented in composable useComments.ts
    // commenting for now to see if any complaints/problems will rise up after removing this from here
    // watch(isLoggedIn, (newLoginValue, oldLoginValue) =>{
    //   if(oldLoginValue !== null && newLoginValue !== oldLoginValue){
    //     myComment.comment = null
    //     myComment.exists = false;
    //     refreshComments(props.loadAll);
    //   }
    // })

    onMounted(() => {
      if (showNewComment.value) {
        onCommentFormOpen();
      }
    });

    return {
      //computed
      commentContent,
      // VARS
      domainStore,
      isInitialized,
      myComment,
      comments,
      loadingAllComments,
      moreCommentsToLoad,
      showNormalComments,
      showCommentForm,
      // Methods
      commentIdTag,
      loadAllComments,
      setshowCommentForm,
      onCommentFormOpen,
      onCommentFormClose,
      onVoteSentWithComment,
      updatedMyComment,
    };
  },

  directives: {
    focus: {
      inserted: function (el) {
        el.focus();
      },
    },
  },

  components: {
    VueLoading,
    Comment,
    CommentForm,
    // DdCommentSupport
  },
};
</script>

<style>
.wdw-comment {
  background-color: whitesmoke;
  margin-bottom: 5px;
}

.wdw-comment.highlighted {
  margin-bottom: 10px;
  min-height: 48px;
  background-color: #fef3db;
  color: #222;
}
.load-more {
  display: flex;
  justify-content: center;
  padding-bottom: 5px;
}
</style>
